import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Link } from '../components/LocalizedLink';
import { getLangFromPath, DATE_LOCALE } from '../i18n/langs';
import { blogUi, localizePost } from '../data/blogTranslations';
import { Calendar, Tag, User, ArrowLeft } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import { renderBlogContent, extractFaq } from '../lib/blogContent';
import { SEO } from '../lib/seo';

const RELATED: Record<string, string> = {
  es: 'Artículos relacionados',
  ca: 'Articles relacionats',
  en: 'Related articles',
  fr: 'Articles similaires',
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const ui = blogUi[lang];
  const found = blogPosts.find((p) => p.slug === slug);
  const post = found ? localizePost(found, lang) : null;

  if (!post) {
    return (
      <div className="py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-8">{ui.notFound}</h1>
          <Link to="/blog" className="text-yellow-500 hover:text-yellow-600 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            {ui.back}
          </Link>
        </div>
      </div>
    );
  }

  // Enlazado interno: artículos con más etiquetas en común (y los más recientes)
  const related = blogPosts
    .filter((p) => p.slug !== post.slug)
    .map((p) => ({ p, score: p.tags.filter((t) => found!.tags.includes(t)).length }))
    .sort((a, b) => b.score - a.score || b.p.date.localeCompare(a.p.date))
    .slice(0, 3)
    .map(({ p }) => localizePost(p, lang));

  const faq = extractFaq(post.content);
  const faqSchema = faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        datePublished={post.date}
      />
      <div className="py-16">
        <div className="container mx-auto px-4">
          <Link to="/blog" className="text-yellow-500 hover:text-yellow-600 flex items-center gap-2 mb-8">
            <ArrowLeft className="w-4 h-4" />
            {ui.back}
          </Link>

          <article>
            <img
              src={post.image}
              alt={post.imageAlt}
              title={post.imageAlt}
              width={1200}
              height={800}
              className="w-full h-[400px] object-cover rounded-lg mb-8"
            />

            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString(DATE_LOCALE[lang], {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author}
              </span>
            </div>

            <h1 className="text-4xl font-bold mb-8">{post.title}</h1>

            <div
              className="prose max-w-none mb-8"
              dangerouslySetInnerHTML={{ __html: renderBlogContent(post.content) }}
            />

            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-600"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>

            {faqSchema && (
              <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
            )}
          </article>

          {related.length > 0 && (
            <section className="mt-12 border-t pt-8">
              <h2 className="text-2xl font-bold mb-6">{RELATED[lang] || RELATED.es}</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="block group">
                    <img
                      src={r.image}
                      alt={r.imageAlt}
                      loading="lazy"
                      className="w-full h-40 object-cover rounded-lg mb-3"
                    />
                    <h3 className="font-semibold group-hover:text-yellow-600">{r.title}</h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

export default BlogPost;
